// @flow

import React, { Fragment } from 'react';

import { Link } from '@zapier/design-system';

import help from 'app/get-help/components/blocks/Help';
import radio from 'app/get-help/components/blocks/Radio';
import {
  getDefaultFields,
  getUserFields,
} from 'app/get-help/content/nodes/common';
import { ZENDESK_CUSTOM_FIELD_IDS } from 'app/get-help/constants';


const GLOBAL_APP_COUNT = '5,000';

const createZapierChild = (isLoggedIn: boolean) => {
  const zapierAndAboutUserFields = [
    ...getUserFields(isLoggedIn),
    {
      id: 'message',
      type: 'text',
      label: `Please let us know what questions you have or what you'd like to do`,
      details: `The more details you can provide, the more tailored support we'll be able to give you. For example: what kinds of tasks would you like to automate with us? Which apps are you using?`,
      isMultiline: true,
    },
  ];

  return radio({
    id: 'zapier',
    label: 'What can we help you with?',
    zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.ABOUT_WHAT_CAN_WE_HELP_WITH,
    items: [
      {
        value: 'start',
        label: 'Getting started with Zapier',
        zendeskCustomFieldValue: 'cf-getting-started',
        child: help({
          docs: {
            title: 'Learn how Zapier works or browse popular Zaps',
            body: () => (
              <p>
                Zapier is a tool that allows you to connect apps you use every
                day to automate tasks and save time. You can connect any of our{' '}
                {GLOBAL_APP_COUNT}+ integrated apps together to make your own
                automations. To create your first Zap,{' '}
                <Link href="/help/create/basics/create-zaps">
                  check out our tutorial here
                </Link>
                . If you‘d like some inspiration for your workflows,{' '}
                <Link href="/learn/automate-apps-examples/">
                  here are some popular Zaps by category
                </Link>
                . You can always see Zap suggestions tailored for you, explore
                popular workflows, and create new ones on your{' '}
                <Link href="/app/home">home page here</Link>.
              </p>
            ),
          },
          contact: zapierAndAboutUserFields,
        }),
      },
      {
        value: 'about',
        label: 'Understanding Zapier',
        zendeskCustomFieldValue: 'cf-understanding-zapier',
        child: help({
          docs: {
            title: 'Learn the key concepts in Zapier',
            body: () => (
              <p>
                Zapier is a tool that allows you to connect apps you use every
                day to automate tasks and save time. You can connect any of our{' '}
                {GLOBAL_APP_COUNT}+ integrated apps together to make your own
                automations. To learn more and get started,{' '}
                <Link href="/help/create/basics/understand-key-concepts-in-zapier">
                  check out our guide here
                </Link>
                .
              </p>
            ),
          },
          contact: zapierAndAboutUserFields,
        }),
      },
      {
        value: 'app',
        label: 'Requesting an app to be added to Zapier',
        zendeskCustomFieldValue: 'cf-requesting-an-app',
        child: help({
          docs: {
            title: 'Vote for the app or request the app',
            body: () => (
              <p>
                To check for an app that you can vote for, go to our{' '}
                <Link href="/apps">App Directory</Link> and search for the name
                of the app. If it appears with the label "Upcoming," you can
                select it and then click <em>Add My Request</em> to vote for the
                app. If you don't see it appear, contact support and let us
                know, and we can record your vote!
              </p>
            ),
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              id: 'website',
              type: 'text',
              label: `What's the website for the app you're requesting?`,
              isRequired: true,
              placeholder: 'https://',
            },
            {
              id: 'relationship',
              type: 'text',
              isMultiline: true,
              label: `How do you know the company?`,
            },
            {
              id: 'message',
              type: 'text',
              isMultiline: true,
              label: `Please tell us more about your request.`,
              details: `What kind of tasks would you like to automate with this app? What are the issues you hope to resolve?`,
            },
          ],
        }),
      },
      {
        value: 'feature',
        label: 'Question about a specific app on Zapier',
        zendeskCustomFieldValue: 'cf-specific-app-question',
        child: help({
          contact: [
            ...getUserFields(isLoggedIn),
            {
              id: 'appName',
              type: 'text',
              label: `What's the name of the app?`,
              isRequired: true,
            },
            {
              id: 'message',
              type: 'text',
              isMultiline: true,
              label: `How can we help?`,
              details: `What kinds of tasks would you like to automate with this app? What are the issues you hope to resolve?`,
            },
          ],
        }),
      },
      {
        value: 'security',
        label: 'Security and data processing',
        zendeskCustomFieldValue: 'cf-security-and-data',
        child: help({
          docs: {
            title: 'Review Zapier’s security practices and privacy policy',
            body: () => (
              <Fragment>
                <p>
                  You can find all the current information about our{' '}
                  <Link href="/help/account/data-management/data-privacy-at-zapier">
                    security practices here
                  </Link>
                  . This includes information about data processing, storage,
                  and more. We are GDPR compliant but not HIPAA compliant.
                </p>
                <p>
                  For a more detailed look, check out{' '}
                  <Link href="/privacy">our privacy policy</Link>.
                </p>
              </Fragment>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
    ],
  });
};

export default createZapierChild;
